import { FacetField, FilterOp, FilterState, Item, Member } from './types';
import { UNASSIGNED } from './constants';

const stringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const FACET_FIELDS: FacetField[] = ['statuses', 'priorities', 'types', 'assignees', 'sources', 'labels'];

/** Coerce an untrusted `ops` blob into a valid per-facet operator map (only 'isNot' entries are kept;
 *  everything else defaults to 'is' by absence). Returns undefined when empty so the shape stays clean. */
const opsFrom = (v: unknown): FilterState['ops'] => {
  const src = (v ?? {}) as Record<string, unknown>;
  const out: Partial<Record<FacetField, FilterOp>> = {};
  for (const k of FACET_FIELDS) if (src[k] === 'isNot') out[k] = 'isNot';
  return Object.keys(out).length ? out : undefined;
};

/** Read a facet's operator, defaulting to "is" (so older filters/links without `ops` behave unchanged). */
export const facetOp = (f: FilterState, field: FacetField): FilterOp => f.ops?.[field] ?? 'is';

/**
 * Coerce an untrusted blob (a persisted localStorage value, or a server-saved view's
 * query) into a well-formed FilterState. Guards the shared filter state against a
 * malformed/legacy shape (e.g. `search: null`, a non-array facet) that would otherwise
 * crash consumers calling `.trim()` / `.length` on it.
 */
export const sanitizeFilters = (raw: unknown): FilterState => {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    search: typeof r.search === 'string' ? r.search : '',
    statuses: stringArray(r.statuses),
    priorities: stringArray(r.priorities),
    types: stringArray(r.types),
    assignees: stringArray(r.assignees),
    sources: stringArray(r.sources),
    labels: stringArray(r.labels),
    ops: opsFrom(r.ops),
  };
};

/** True when any search/filter narrows the item set (so a view should switch out of tree mode). */
export const filtersActive = (f: FilterState): boolean =>
  !!f.search.trim() ||
  f.statuses.length > 0 ||
  f.priorities.length > 0 ||
  f.types.length > 0 ||
  f.assignees.length > 0 ||
  f.sources.length > 0 ||
  f.labels.length > 0;

/**
 * Single source of truth for the client-side filter predicate, shared by the Table,
 * Kanban and Timeline views so all narrow the backlog identically. Search matches title /
 * key / description plus the assignee's and PdM's names; the multiselects are AND across
 * facets, OR within a facet.
 *
 * `members` (id → Member) is what lets a name query like "catalin" find that person's
 * items — items only store member *ids*, so without the lookup a name matches nothing and
 * every row dims. It's optional so callers without member data still get title/key search.
 */
export const matchesFilters = (it: Item, f: FilterState, members?: Map<string, Member>): boolean => {
  const q = f.search.trim().toLowerCase();
  if (q) {
    const names = members
      ? [it.assignee_id, it.pdm_id].map((id) => (id ? members.get(id)?.name ?? '' : '')).join(' ')
      : '';
    if (!`${it.title} ${it.description} ${it.key} ${names}`.toLowerCase().includes(q)) return false;
  }
  // Each facet is "is" (keep matches) or "is not" (exclude matches). `hit` = the item matches the
  // selected values for that facet; then "is" excludes non-hits and "is not" excludes hits.
  const fails = (field: FacetField, hit: boolean): boolean => (facetOp(f, field) === 'isNot' ? hit : !hit);
  if (f.statuses.length && fails('statuses', f.statuses.includes(it.status))) return false;
  if (f.priorities.length && fails('priorities', f.priorities.includes(it.priority))) return false;
  if (f.types.length && fails('types', f.types.includes(it.type))) return false;
  if (f.assignees.length) {
    // The `Unassigned` sentinel matches items with no assignee; ids match as before.
    const hit = it.assignee_id ? f.assignees.includes(it.assignee_id) : f.assignees.includes(UNASSIGNED);
    if (fails('assignees', hit)) return false;
  }
  // Array-overlap facets: "is" keeps items sharing ≥1 selected value; "is not" excludes them (i.e. keeps
  // items that have NONE of the selected sources/labels).
  if (f.sources.length && fails('sources', !!it.source?.some((s) => f.sources.includes(s)))) return false;
  if (f.labels.length && fails('labels', !!it.labels?.some((l) => f.labels.includes(l)))) return false;
  return true;
};

/** Filter a list, short-circuiting to the same array reference when nothing is active. */
export const filterItems = (items: Item[], f: FilterState, members?: Map<string, Member>): Item[] =>
  filtersActive(f) ? items.filter((it) => matchesFilters(it, f, members)) : items;
